/**
 * The **mech** spawn adapter: a roster mech → `compute` → a fully-frozen combatant base.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { CombatantBase, MechData, PilotData, WeaponProfile } from "@lancer-vtt/domain";
import { EJECT_LOCKOUT_RESOURCE, REPAIR_CAP_RESOURCE, destroyedItemResourceKey, resolvePrintedMechValues, talentIntegratedGrants } from "@lancer-vtt/domain";
import { ok, err } from "@lancer-vtt/shared";
import { coreActionIdsFor } from "../coreActionCatalog";
import { compute } from "../../compute";
import { buildAffordances, freezeActionIds, freezeActionCaps, freezeAuthoredReach, freezeLimitedSystemPools, freezeOverlayAffordances, freezeTalentAffordances, frameIntegratedWeaponIds } from "./actionFreeze";
import { frameEffectAffordanceSources, freezeFrameTraitEffectIds, freezeFrameTraitCaps, freezePilotEffectIds } from "./effectFreeze";
import type { LoadedOverlay } from "../effects/loader";
import { freezeImprovisedAttack, freezeMounts, freezeSystems, freezeWeapons, resolvePilotDroneHpBonus, resolvePilotRangeBonus, resolveScopedRangeBonuses, resolveTalentWeaponBonuses } from "./loadoutFreeze";
import { freezePilotBlock } from "./pilot";
import { applyPrototypeConfig } from "./prototype";
import type { SpawnResult } from "./types";

/** The mech's integrated weapon ids: frame-integrated first, then talent-integrated (one `integrated:N` namespace). */
export function mechIntegratedWeaponIds(pilotData: PilotData, mech: MechData, compendium: Compendium): string[] {
  const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
  return [...frameIntegratedWeaponIds(frame), ...talentIntegratedGrants(pilotData, compendium).weaponIds];
}

/**
 * Every weapon profile a mech deploys with, with the pilot's range and talent bonuses applied —
 * the one freeze `spawnMech` uses, exported so the roster-side unit sheet (the BUILD posture)
 * prints the same numbers the fight will.
 */
export function freezeMechWeapons(args: { pilotData: PilotData; mech: MechData; compendium: Compendium; integratedWeaponIds?: string[] }): WeaponProfile[] {
  const { pilotData, mech, compendium } = args;
  // The Engineer's prototype weapon takes the mech's Full Repair settings (`spawn/prototype.ts`).
  return applyPrototypeConfig(
    freezeWeapons({
      mech,
      pilot: pilotData,
      compendium,
      integratedIds: args.integratedWeaponIds ?? mechIntegratedWeaponIds(pilotData, mech, compendium),
      rangeBonus: resolvePilotRangeBonus(pilotData, compendium),
      talentBonuses: resolveTalentWeaponBonuses(pilotData, compendium),
      scopedRangeBonuses: resolveScopedRangeBonuses({ mech, pilot: pilotData, compendium }),
    }),
    mech.prototype,
  );
}

/**
 * Spawn a combatant from a roster mech: `pilotData.mechs[mechIdx]` → {@link
 * compute} → frozen base. The caller mints the combatant `id` (so the reducer
 * stays deterministic and the inverse can name it — the same id discipline the
 * roster commands use).
 *
 * `compute` **omits a mech with no/unresolved frame** from its output (a
 * half-built mech has no stats). `spawnMech` treats that as a clean rejection —
 * "can't deploy a frameless mech" — not a crash. Armor is read from the frame stats
 * directly: `compute`'s `ComputedMechStats` folds HASE/bonuses into the live combat stats but
 * does not surface armor (no HASE/grit term touches it), so we pull the frame's base armor.
 */
export type SpawnMechArgs = {
  combatantId: string;
  pilotData: PilotData;
  mechIdx: number;
  compendium: Compendium;
  /**
   * The loaded action overlay, **optional**. Present ⇒ {@link freezeOverlayAffordances} reads
   * each equipped system's authored `action` clauses and freezes a Full-Repair-grain Limited pool
   * (`limited: {max}`) for every OVERLAY-MINTED id whose clause carries a
   * `limitedUses` facet — the only way `ms_custom_paint_job:0`-class ids (no compendium
   * `actions[]`/`deployables[]`/`tg_limited` to derive from) can ever be capped
   * (`projects/track-damage-event-family.md` D2).
   *
   * **Absent ⇒ no such cap is frozen**, deliberately: the cap must be FROZEN, never derived at
   * check time, because the commit extractor (`limitedSystemUsedCounts`) reads the frozen map with
   * no overlay in hand — a cap derived elsewhere would be invisible to it and the charge would
   * silently recharge on every redeploy ("once per encounter", not "once per Full Repair"). A
   * caller with no overlay honest-degrades to an uncapped row rather than guessing, the same
   * opt-in convention `mechDeployRows`' own `overlay?` uses.
   */
  overlay?: LoadedOverlay;
};

export function spawnMech(args: SpawnMechArgs): SpawnResult {
  const { combatantId, pilotData, mechIdx, compendium, overlay } = args;
  const mech = pilotData.mechs[mechIdx];
  if (!mech) {
    return err(`spawnMech: pilot has no mech at index ${mechIdx}`);
  }
  if (!mech.frameId) {
    return err(
      `spawnMech: mech "${mech.name}" has no frame selected; can't deploy a frameless mech`,
    );
  }
  // A printed mech carrying a `destroyed` marker (wreck or vaporized) can't be deployed — the
  // pilot must run it through recovery first (`restore_destroyed_mech` for a wreck, `reprint`
  // for a vaporized loss).
  const destroyedMarker = pilotData.printedMechs?.[mech.id]?.destroyed;
  if (destroyedMarker) {
    return err(
      `spawnMech: mech "${mech.name}" is destroyed (${destroyedMarker}); can't deploy — restore or reprint it first`,
    );
  }

  const { mechs, pilot: computedPilot } = compute(pilotData, compendium);
  const computed = mechs.find((m) => m.id === mech.id);
  if (!computed) {
    // compute() omits a mech whose frame doesn't resolve in the compendium.
    return err(
      `spawnMech: mech "${mech.name}" frame ${mech.frameId} did not resolve in the compendium; can't deploy`,
    );
  }

  // Armor isn't folded by compute() (no HASE term), so read it from the frame.
  const frame = compendium.getById("frame", mech.frameId);
  const armor = typeof frame?.stats.armor === "number" ? frame.stats.armor : 0;
  // Structure/stress are flat frame stats — player mechs are 4/4 in core data — and, like
  // armor, aren't folded by compute(). Read them off the frame; default to 4 (the universal
  // player-mech value) when a frame omits them.
  const maxStructure = typeof frame?.stats.structure === "number" ? frame.stats.structure : 4;
  const maxStress = typeof frame?.stats.stress === "number" ? frame.stats.stress : 4;

  // Talent-integrated weapon ids (Prototype Weapon, Fuel Rod Gun — exclusive-replace per rank)
  // merged AFTER the frame-integrated ids into ONE list every weapon-facing freeze below shares
  // — frame-integrated first, then talent-integrated, deterministic order, so `integrated:N`
  // stays one sequential instance-key namespace across both sources (never two
  // separately-numbered ranges).
  const integratedWeaponIds = mechIntegratedWeaponIds(pilotData, mech, compendium);

  const s = computed.stats;
  const base: CombatantBase = {
    maxHp: s.hp,
    maxHeat: s.heatcap,
    armor,
    evasion: s.evasion,
    edef: s.edef,
    speed: s.speed,
    size: s.size,
    // The forced-save DC this mech imposes when it makes another combatant save — the
    // **computed** save target, not a re-derived formula. `compute()` already folds
    // `frame.stats.save + grit` and runs the `save` BonusId pass over it
    // (`compute.ts`'s `apply("save", …)`), so `s.save` is the whole truth and the only
    // correct source. The formula that used to sit here (`10 + computedPilot.grit`)
    // silently discarded two things `decisions/0122` says the data outranks a formula
    // on: the **nine of 29 frames whose printed Save Target is not 10** (Vlad, Black
    // Witch, Dusk Wing, Goblin, Minotaur, Napoleon, Tokugawa at 11; Gorgon, Iskander at
    // 12), and `cb_the_lesson_of_the_open_door`'s **+2 SAVE TARGET**. Frozen at spawn;
    // the resolver reads the *attacker's* `saveTarget` as the default DC for a
    // `force_save`. See `decisions/0163`.
    saveTarget: s.save,
    // A mech's weapon-attack to-hit bonus is its **grit** (`ceil(level/2)`) — the flat bonus
    // the resolver adds to the d20. Frozen at spawn alongside the rest of the block.
    attackBonus: computedPilot.grit,
    // A mech's **tech** attack rolls its TECH ATTACK stat instead of Grit (Core, "TECH ATTACK").
    // `compute()` already folds `frame.tech_attack + Systems` and runs the `tech_attack` bonus
    // pass over it, so `s.techAttack` is the whole truth — never re-derived here.
    techAttackBonus: s.techAttack,
    // The mech's **Sensors range** (its frame Sensors stat, folded by compute) bounds how far a
    // tech attack reaches — the tech-attack analogue of weapon reach. Frozen at spawn; the
    // room's tech-range gate reads it.
    sensors: s.sensorRange,
    // The pilot's raw AGILITY + SYSTEMS HASE skills — the two axes a RAW Search contest reads
    // (`1d20 + searcher.systems` vs `1d20 + target.agility`). Frozen here exactly like
    // `sensors`: the raw `mechSkills` value, not a derived stat (evasion folds agility, but
    // Search needs the unmodified axis). The Hidden/Search machinery reads them off the frozen
    // combatant, never the live roster pilot.
    agility: pilotData.mechSkills.agi,
    systems: pilotData.mechSkills.sys,
    // The pilot's raw HULL HASE skill — the axis a contested-HULL check (the Grapple break)
    // rolls on both sides. Frozen exactly like agility/systems: the raw `mechSkills` value, not
    // a derived stat.
    hull: pilotData.mechSkills.hull,
    // The pilot's raw ENGINEERING HASE skill. A mech carries all FOUR axes because a mech save
    // rolls the relevant one (`:1093`, `:9777` — `decisions/0163`), and burn and dangerous
    // terrain are both ENGINEERING saves. This field used to be omitted deliberately
    // ("mechs never carry it… only the flat `base.save` bonus every self-save uses"), which
    // is exactly why burn's ENGINEERING check never read ENGINEERING.
    engineering: pilotData.mechSkills.eng,
    // The consequence tracks (rulebook "STRUCTURE DAMAGE" / "OVERHEATING AND STRESS"). Frozen
    // at spawn; the resolver reads them when HP hits 0 / heat exceeds the cap. Player mechs
    // are 4/4.
    maxStructure,
    maxStress,
    // A mech is never Biological — its destroy always converts to a wreck
    // (`wreckConversion.ts`'s `destroyedConversionKind` reads this).
    destructionOutcome: "wreck",
    // A mech is always PC-side (`sideOf` reads this capability, never `kind`).
    allegiance: "pc",
    // The computed Repair Cap *max*, frozen alongside the other maxima so the combat inspector
    // can show "remaining / max" off the bound combatant. The *remaining* rides the generic
    // resource (`current.resources[REPAIR_CAP_RESOURCE]`, seeded just below); this is the
    // matching ceiling.
    repairCapMax: s.repcap,
    // Every equipped weapon's damage profile (dice/type + AP/overkill/reliable tags), frozen so
    // the resolver reads damage off the combatant, never the compendium. Frame-integrated
    // weapons (the Barbarossa's Apocalypse Rail) deploy automatically alongside the loadout's
    // mounted weapons. Neurolink Targeting's `+3 Range` is resolved off the pilot's held core
    // bonuses and threaded through here so every Range-type band on every frozen weapon (loadout
    // + frame-integrated + talent-integrated) gets the bump — the attack-range gate
    // (`checkAttackRange`) reads it straight off the frozen combatant, never re-deriving from
    // the pilot mid-fight.
    weapons: freezeMechWeapons({ pilotData, mech, compendium, integratedWeaponIds }),
  };
  // A talent that rewrites the Improvised Attack (Brawler 2) freezes the carrier's own profile.
  const improvisedAttack = freezeImprovisedAttack(resolveTalentWeaponBonuses(pilotData, compendium));
  if (improvisedAttack) base.improvisedAttack = improvisedAttack;

  // The pilot's active-effect-bearing entity ids (cumulative talent-rank ids + core-bonus ids),
  // frozen so the resolver gathers a mech's talent/core-bonus triggered effects + conditional
  // modifiers off the frozen combatant — the active counterpart to the passive stat bonuses
  // `compute` folds into `base`. Omit the field entirely when the pilot carries none.
  const pilotEffectIds = freezePilotEffectIds(pilotData);
  if (pilotEffectIds.length > 0) base.pilotEffectIds = pilotEffectIds;

  // The frame's trait-bearing entity ids (synthesized `${frameSlug}_${traitSlug}` ids — traits
  // carry no native compendium id), frozen so the resolver gathers a mech's frame-trait
  // triggered effects/modifiers off the frozen combatant, through the SAME gather seam as
  // pilotEffectIds (`combatantEffectIds` merges the two lists). Omit the field when the frame
  // carries no traits (most frames).
  const frameEffectIds = freezeFrameTraitEffectIds(frame);
  if (frameEffectIds.length > 0) base.frameEffectIds = frameEffectIds;

  // Pilot-gear effect ids deliberately do NOT freeze onto the mech. RAW: no fielded pilot-gear
  // item has an effect that acts on the MECH — Frag Grenades/Patch/Corrective are things the
  // PILOT throws/applies, and Mobility Hardsuit's Flying-on-Boost clause grants flight to the
  // pilot who wears the suit, not to whichever mech that pilot happens to be piloting. They
  // freeze in `spawnPilot` (a direct on-foot deploy) and `spawnDismountedPilot` (via
  // `FrozenPilotBlock.pilotGearEffectIds`, frozen just below by {@link freezePilotBlock})
  // instead. Freezing them here inverts the vector: a mech's action bar mints and fires
  // pilot-gear rows (uncapped — only the composite ids `spawnPilot`'s
  // `freezePilotGearActionIds` freezes carry a cap on their affordance record), a mech gains Flying from its pilot's Mobility Hardsuit
  // on Boost, and an ejected/dismounted pilot has no gear affordances at all.

  // The mech's loadout-granted action ids (composite `<itemId>:<actionIndex>` for each equipped
  // system/weapon carrying an `actions[]` array), so the per-combatant enumerator can merge those
  // granted actions onto the bar alongside the core actions. Ids-only (resolvable derived
  // downstream); unloaded-pack items honest-degrade. Omit when empty. Freezes the ids AND the
  // reach of any that declares one (`actionRanges` — the number the activate path's `site` gate
  // reads, since the pure reducer has no compendium handle).
  const droneHpBonus = resolvePilotDroneHpBonus(pilotData, compendium);
  if (droneHpBonus > 0) base.droneHpBonus = droneHpBonus;
  const { ids: grantedKeys, sources: grantedSources, names: grantedNames, ranges: rawRanges, techAttackActions } = freezeActionIds(mech, compendium, { integratedWeaponIds, sensors: base.sensors });
  // A reach RAW prints only in prose rides the overlay clause (`ActionEffect.reach`).
  const actionRanges = overlay ? freezeAuthoredReach({ ownerId: combatantId, keys: grantedKeys, ranges: rawRanges, overlay, sensors: base.sensors }) : rawRanges;
  if (Object.keys(actionRanges).length > 0) base.actionRanges = actionRanges;
  // The frozen fact the tech-attack legality wall + economy read — sparse, like `actionRanges`
  // just above.
  if (Object.keys(techAttackActions).length > 0) base.techAttackActions = techAttackActions;

  // The slot-resolved mount structure + the equipped-systems inventory — the shared seam System
  // Trauma and Barrage's Aux rider both consume. Omit when empty (an unbuilt loadout / systemless
  // mech), mirroring every other optional freeze above. Frozen BEFORE the affordance table below
  // because the overlay-mint arm walks this exact list (the same scoping `carriedAffordances`
  // uses), not `loadout.systems` — a talent-integrated system is in one and not the other.
  const mounts = freezeMounts(mech, pilotData, compendium);
  if (mounts.length > 0) base.mounts = mounts;
  const equippedSystems = freezeSystems(mech, pilotData, compendium);
  if (equippedSystems.length > 0) base.equippedSystems = equippedSystems;

  // The rate and pool joins, **spawn-LOCAL** — they feed `buildAffordances` below and are never
  // themselves frozen onto `base` (`affordance-table` Phase 3.1 deleted `base.actionUsageCaps`; the
  // facts now live inline on the record they belong to). Two arms, keyed disjointly: each granted
  // action's OWN frequency rate and `Limited {N}` pool off its compendium entry (both, when both
  // are printed: `decisions/0202`), and the frame's OWN trait cadences (the Everest's Initiative:
  // `1/scene`) plus its core activation's Core Power pool, which `freezeFrameTraitCaps` keys by the
  // trait's synthesized id (`frameEffectIds`' convention) so `everest_initiative`'s cap rides the
  // exact same `checkGrantedActionLegality`/`spend_action_use` gate a compendium-item-granted
  // action's cap does — no new gate/consume code, only this freeze.
  const granted = freezeActionCaps(mech, compendium, integratedWeaponIds);
  const frameFacts = freezeFrameTraitCaps(frame);
  const caps = { ...granted.caps, ...frameFacts.caps };
  const limited = { ...granted.limited, ...frameFacts.limited };

  // **The frozen affordance table** (`decisions/0176`) — the ONE frozen description of this mech's
  // action economy since Phase 3.1 deleted the three parallel maps. Five arms, in order:
  //   1. the granted class, in `freezeActionIds`' walk order (integrated weapons, loadout weapons,
  //      equipped systems);
  //   2. the frame's effect-class records (trait / core-system entries carrying a frozen cadence),
  //      which carry no index because a bare entry-level fire is the only thing that consumes an
  //      entry-level cap correctly;
  //   3. the OVERLAY-MINT arm — a system whose authored clause carries `limitedUses` but whose
  //      compendium record has neither `actions[]` nor `deployables[]`, so arm 1 could never reach
  //      it (`ms_custom_paint_job` is the worked case). It dedupes against arm 1's keys;
  //   4. the talent arm — a talent rank whose action clause authors `limitedUses` (Leader's
  //      Leadership Dice), keyed by the bare rank id;
  //   5. the Limited pool of a `tg_limited` system no earlier arm pools (Walking Armory's Ammo
  //      Case), keyed by the system id.
  //
  // Arm 3 is why this table is not simply arm 1 + arm 2. Its cap used to be frozen into
  // `base.actionUsageCaps` under an id that was in no other frozen list at all; with one record
  // per affordance, a frozen cap with no record is unreadable by the two sites that need it
  // (`structureIntercept.ts`'s charge spend, `tables.ts`'s System-Trauma destroyability), neither
  // of which has an overlay in scope to re-mint from. Omitted entirely when the caller passed no
  // overlay — see `SpawnMechArgs.overlay`.
  //
  // The caps are looked up out of the local join by key rather than re-derived, so a record and
  // the cap that produced it cannot disagree.
  const tableArms = [
    ...buildAffordances({ sources: grantedSources, caps, limited, names: grantedNames }),
    ...buildAffordances({ sources: frameEffectAffordanceSources(frame), caps, limited }),
    ...(overlay ? freezeOverlayAffordances({ ownerId: combatantId, equippedSystems, frozenKeys: grantedKeys, overlay }) : []),
    ...(overlay ? freezeTalentAffordances({ ownerId: combatantId, effectIds: pilotEffectIds, overlay }) : []),
  ];
  const affordances = [...tableArms, ...freezeLimitedSystemPools({ equippedSystems, frozen: tableArms })];
  if (affordances.length > 0) base.affordances = affordances;

  // The mech's CORE action vocabulary, frozen as provenance-namespaced composite ids
  // (`mech:skirmish`, `mech:boost`, …) — `enumerateActions` resolves this frozen set against the
  // global catalog and never reads the combatant's kind. Every kind stamps a non-empty core set
  // (unlike the optional granted `affordances`), so it's unconditional.
  base.coreActionIds = coreActionIdsFor("mech");

  // The on-foot pilot block — every player mech has a piloting pilot by construction (this IS
  // `pilotData`), so this is unconditional (unlike the optional fields above). It's the seam
  // Dismount/Eject/destruction mint the on-foot pilot from.
  base.pilotBlock = freezePilotBlock(pilotData, computedPilot, compendium);

  // Seed `current` from the mech's persisted between-encounter wear
  // (`pilotData.printedMechs[mech.id]`), defaulting each absent track to its pristine max
  // (absent == pristine / Full Repair). Repair Cap remaining rides the generic resource system
  // under `REPAIR_CAP_RESOURCE` (current seeded here; the *max* `s.repcap` is always re-derived
  // from compute, never stored). Heat seeds `0` (Rest-cleared, not carried).
  const printed = pilotData.printedMechs?.[mech.id];
  const seeded = resolvePrintedMechValues(printed, {
    maxHp: base.maxHp,
    maxStructure,
    maxStress,
    repairCapMax: s.repcap,
  });

  // Re-seed the Eject lockout + Impaired on redeploy when the persisted `ejectSpent` marker is
  // set (the mech ejected its pilot and hasn't been Full Repaired since — Full Repair deletes the
  // whole sparse record, clearing this along with it). The same re-seed pattern the `destroyed`
  // marker uses, one field over.
  const ejectSpent = printed?.ejectSpent === true;

  // Re-seed each persisted destroyed-item marker (bare keys in `printed.destroyedItems`) back
  // onto `current.resources` as `destroyed:<key>` — the exact runtime shape `destroy_item` folds
  // in-encounter (`destroyedItemResourceKey`), so a redeployed mech's destruction survives the
  // commit→redeploy round-trip. Absent/empty ⇒ no markers (the ordinary case, and a wreck-commit
  // never populates `destroyedItems` to begin with).
  const destroyedItems = printed?.destroyedItems ?? [];

  // Re-seed each persisted Limited-WEAPON consumed-charge count (`printed.limitedUsed`, bare
  // keys) back onto the per-affordance ledger — the exact runtime shape `canUseLimitedWeapon`
  // reads, mirroring `destroyedItems`'s re-seed one field above.
  // Absent/empty ⇒ no consumed charges (the ordinary case).
  const limitedUsed = printed?.limitedUsed ?? {};

  // Re-seed each persisted Limited-SYSTEM consumed-charge count (`printed.limitedSystemUsed`,
  // granted-action affordance keys OR pool names — every shipped Limited system's pool is named
  // by its bare item id, and both walls read `limited.pool ?? key`) onto the same ledger — the exact runtime shape
  // `canUseSceneAction` reads, mirroring `limitedUsed`'s weapon re-seed one
  // field above. Absent/empty ⇒ no consumed charges (the ordinary case, and — critically — the
  // case for every ordinary RAW "1/scene" ability too, which never persists here at all).
  const limitedSystemUsed = printed?.limitedSystemUsed ?? {};

  // Both persisted counts key into ONE per-affordance ledger (`decisions/0176` ruling 1) — a
  // weapon by its `weaponInstanceKey`, a Limited system by its pool name or granted-action
  // affordance key, two disjoint namespaces in one flat table (a frozen weapon's `instanceId` is
  // always namespaced, `0:0`/`integrated:0`, never a bare item id). This is the only place they land since
  // `affordance-table` Phase 3.1 deleted the `limited:`/`scene_use:` `resources` prefixes they
  // used to be re-seeded into as well; the extractors (`limitedUsedCounts`,
  // `limitedSystemUsedCounts`) read straight back off it on the next commit.
  const affordanceState: Record<string, { uses: number }> = {};
  for (const [key, count] of Object.entries(limitedUsed)) if (count > 0) affordanceState[key] = { uses: count };
  for (const [key, count] of Object.entries(limitedSystemUsed)) if (count > 0) affordanceState[key] = { uses: count };

  return ok({
    id: combatantId,
    kind: "mech",
    name: mech.name,
    base,
    current: {
      hp: seeded.hp,
      heat: 0,
      structure: seeded.structure,
      stress: seeded.stress,
      resources: {
        [REPAIR_CAP_RESOURCE]: seeded.repairsRemaining,
        ...(ejectSpent ? { [EJECT_LOCKOUT_RESOURCE]: 1 } : {}),
        ...Object.fromEntries(destroyedItems.map((key) => [destroyedItemResourceKey(key), 1])),
      },
      ...(Object.keys(affordanceState).length > 0 ? { affordanceState } : {}),
    },
    statuses: ejectSpent ? [{ name: "Impaired", expiry: { kind: "until_removed" } as const }] : [],
    sourceRef: mech.frameId,
    // Write-target breadcrumbs for the post-fight commit (the mech id is the `printedMechs` key;
    // the pilot id names whose roster to write back to). Set once here; never re-resolved for
    // reads (the base is frozen). NPCs leave these undefined.
    sourcePrintedMechId: mech.id,
    pilotId: pilotData.id,
    ...(pilotData.bondmate !== undefined ? { bondmatePilotId: pilotData.bondmate } : {}),
    pos: null, // unplaced until a `move_token` deploys it onto the map.
    // The combatant is controlled by the slot that owns the source pilot — captured once, at
    // spawn, from `pilotData.owner`. `null` (a solo/offline pilot with no owner) ⇒ GM-owned (no
    // player controls it; the GM can act on anything). The permission gate reads this, never the
    // source pilot at check time.
    controllerSlotId: pilotData.owner,
  });
}
